import { Patient } from 'core/models/patientModel';
import { PatientBase } from 'core/models/patientModelBase';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import DOMPurify from 'dompurify';

dayjs.extend(timezone);

export const toFixedTrunc = (num: number, decimals = 2) => {
  if (typeof num === 'number') {
    const factor = 10 ** decimals;
    // 处理负数：使用 Math.trunc 向零取整，或用 Math.floor（向负无穷）
    return Math.trunc(num * factor) / factor;
  }
  return num;
};

export const throttle = function (func: any, delay = 300) {
  let lastTime = 0;
  return function (...args: any[]) {
    const now = Date.now();
    if (now - lastTime >= delay) {
      func.apply(this, args);
      lastTime = now;
    }
  };
};

export const debounce = function (func: any, delay = 700) {
  let timer: any = null;
  return function (...args: any[]) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const getTenantId = () => {
  const tenantData = JSON.parse(localStorage.getItem('tenantData') || '{}');
  return tenantData?.tenantId || '';
};

export const getTenantData = () => {
  const tenantData = JSON.parse(localStorage.getItem('tenantData') || '{}');
  return tenantData || {};
};

export const isBlantString = (value: string): boolean => {
  if (value == null || value === undefined || value === '') {
    return true;
  }
  return false;
};

export const isNotEmptyObject = (obj: any): boolean => {
  if (typeof obj === 'object') {
    if (Object.keys(obj).length > 0) {
      return true;
    }
  }
  return false;
};

export const timeZone = (): string => {
  return dayjs.tz.guess();
};

export interface TextSizeVo {
  width: number;
  height: number;
}

export function textSize(
  fontSize: string,
  fontFamily: string,
  text: string,
): TextSizeVo {
  const span = document.createElement('span');
  const result: TextSizeVo = {
    width: 0,
    height: 0,
  };
  result.width = span.offsetWidth;
  result.height = span.offsetHeight;
  span.style.visibility = 'hidden';
  span.style.fontSize = fontSize;
  span.style.fontFamily = fontFamily;
  span.style.display = 'inline-block';
  document.body.appendChild(span);
  if (typeof span.textContent !== 'undefined') {
    span.textContent = text;
  } else {
    span.innerText = text;
  }
  result.width = parseFloat(window.getComputedStyle(span).width) - result.width;
  result.height =
    parseFloat(window.getComputedStyle(span).height) - result.height;
  span.remove();
  return result;
}

export const formatPatientName = (
  item: PatientBase & { email?: string },
  type = '',
) => {
  if (!item.subjectId && !item.firstName && !item.lastName) {
    if (item?.email) {
      return item?.email;
    }
    return '-';
  }
  if (type === 'simple') {
    return item.subjectId || `${item.firstName || ''} ${item.lastName || ''}`;
  }
  let name = '-';
  if (item?.subjectId != null) {
    name = item?.subjectId;
  }
  let patientName = null;
  if (item?.firstName != null && item?.lastName != null) {
    patientName = `${item?.firstName} ${item?.lastName}`;
  }
  if (item?.firstName != null && item?.lastName == null) {
    patientName = `${item?.firstName}`;
  }
  if (item?.firstName == null && item?.lastName != null) {
    patientName = `${item?.lastName}`;
  }
  if (name !== '' && patientName != null) {
    name = `${name} / ${patientName}`;
  }
  if (name !== '' && patientName == null) {
    name = `${name}`;
  }
  if (name === '' && patientName != null) {
    name = `${patientName}`;
  }
  return name;
};

export const getOS = () => {
  const { userAgent } = navigator;
  let os = 'Unknown OS';

  if (userAgent.indexOf('Win') !== -1) {
    os = 'Windows';
  } else if (userAgent.indexOf('Mac') !== -1) {
    os = 'macOS';
  } else if (userAgent.indexOf('Linux') !== -1) {
    os = 'Linux';
  } else if (userAgent.indexOf('X11') !== -1) {
    os = 'Unix';
  }

  return os;
};

export const safelyString = (text: string) => {
  return DOMPurify.sanitize(text);
};

export const camelToSnake = (str: string) => {
  if (str) {
    // 使用正则表达式匹配所有大写字母（A-Z）
    // 对于匹配到的每一个大写字母，在它前面加上下划线 '_'，并将其转为小写。
    return str.replace(/[A-Z]/g, (letter: string) => {
      return `_${letter.toLowerCase()}`;
    });
  }
  return str;
};

export const removeEmptyKey = (obj: Record<string, any>) => {
  const newObj = { ...obj };
  Object.keys(newObj).forEach((item) => {
    if (newObj[item] === 0 || newObj[item] === '') {
      delete newObj[item];
    }
  });
  return newObj;
};

export const initQuery = (obj: Record<string, any> = {}) => {
  const newObj: any = {};
  Object.keys(obj).forEach((item: string) => {
    const value = obj[item];
    if (Array.isArray(value)) {
      newObj[item] = value.map((val) =>
        val?.value != null ? val?.value : val,
      );
    } else {
      newObj[item] = value?.value != null ? value?.value : value;
    }
  });
  return newObj;
};

export const isObjectEmptyOrUndefined = (obj: Record<string, any>) => {
  // 1. 获取对象的所有值
  const values = Object.values(obj);

  // 2. 检查：如果没有值 (values.length === 0) -> 对象是空对象 {}
  //    或者：所有值都满足 v === undefined
  return values.length === 0 || values.every((v) => v === undefined);
};

export const commonFilterOption = (input: string, option: any) => {
  const { children, label } = option;
  const children_ = label || children.toString();
  return children_.toLowerCase().indexOf(input.toLowerCase()) >= 0;
};

export const dateFormat = 'YYYY-MM-DD HH:mm:ss';

export const numberFixed = (number: number, count = 2) => {
  return Math.floor(number * 10 ** 2) / 10 ** 2;
};

export const handleDrawerErrorScroll = (error: any) => {
  const firstError = error?.errorFields?.[0];
  if (firstError) {
    const errorItem: any = document.querySelector(`.ant-form-item-has-error`);

    if (errorItem) {
      // 2. 获取 Drawer 的滚动容器
      const scrollContainer = document.querySelector('.ant-drawer-body');

      if (scrollContainer) {
        // 3. 计算错误项相对于滚动容器顶部的位置，并执行平滑滚动
        const top = errorItem?.offsetTop - 20; // 减 20px 留出边距
        scrollContainer.scrollTo({
          top,
          behavior: 'smooth',
        });
      }
    }
  }
};

export const isMobileApp = () => {
  return navigator.userAgent.indexOf('VivaAI-Mobile') !== -1;
};
